import apiClient from '../client'
import { buildJsonApiQuery, normalizeResponse } from '@/utils/jsonapi'

/**
 * Dashboards API Module
 * Handles JSON:API requests/responses for dashboards
 */

/**
 * Fetch all dashboards for current tenant
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.pageSize - Items per page
 * @param {Object} options.filters - Filter criteria
 * @param {string} options.include - Related resources to include
 * @returns {Promise<Object>} Normalized dashboard data with meta
 */
export async function fetchDashboards(options = {}) {
  const params = buildJsonApiQuery(options)
  const response = await apiClient.get('dashboards/', { params })

  // Return raw JSON:API response (normalization moved to slice)
  return response.data
}

/**
 * Fetch a single dashboard by ID
 * @param {string} id - Dashboard UUID
 * @param {Object} options - Query options
 * @param {string} options.include - Related resources to include (e.g., 'widgets')
 * @returns {Promise<Object>} Normalized dashboard object with full widget data
 */
export async function fetchDashboard(id, options = {}) {
  // Always include widgets AND their metrics for dashboard detail
  const includeWidgets = options.include ? `widgets.metrics,${options.include}` : 'widgets.metrics'
  const params = buildJsonApiQuery({ ...options, include: includeWidgets })
  const response = await apiClient.get(`dashboards/${id}/`, { params })

  // Return raw JSON:API response (normalization moved to slice)
  return response.data
}

/**
 * A dashboard's id and name only (no widgets), e.g. to title a link to it.
 * @param {string} id - Dashboard UUID
 * @returns {Promise<{id: string, name: string|null}>}
 */
export async function fetchDashboardName(id) {
  const response = await apiClient.get(`dashboards/${id}/`)
  const resource = response?.data?.data
  const name = resource?.attributes?.name
  return { id, name: typeof name === 'string' && name.trim() ? name : null }
}

/** Restore a saved dashboard; the server checks the active-dashboard allowance. */
export async function activateDashboard(id) {
  const response = await apiClient.post(`dashboards/${id}/activate/`, {})
  return response.data
}

/** Keep a dashboard's configuration while removing it from active use. */
export async function deactivateDashboard(id) {
  const response = await apiClient.post(`dashboards/${id}/deactivate/`, {})
  return response.data
}

/**
 * Create a new dashboard
 * @param {Object} dashboardData - Dashboard data
 * @param {string} dashboardData.name - Dashboard name
 * @param {string} dashboardData.description - Dashboard description
 * @param {boolean} dashboardData.is_organization_wide - Visibility
 * @returns {Promise<Object>} Normalized created dashboard
 */
export async function createDashboard(dashboardData) {
  const payload = {
    data: {
      type: 'dashboard',
      attributes: dashboardData,
    },
  }

  const response = await apiClient.post('dashboards/', payload)

  // Return raw JSON:API response (normalization moved to slice)
  return response.data
}

/**
 * Create a dashboard from a starter template (H3)
 *
 * Unlike createDashboard, the request body is not a JSON:API attributes
 * payload -- E3's contract is a bare `{key}` naming which starter to build.
 * The response is still the standard dashboard JSON:API resource (same
 * shape as createDashboard's), plus `meta.skipped_widgets`: the titles of
 * any starter widgets that were left out because their metric isn't set up
 * yet.
 * @param {'weekend_overview'|'board_view'} key - Starter dashboard key
 * @returns {Promise<Object>} Raw JSON:API response ({data, meta})
 */
export async function createDashboardFromStarter(key) {
  const response = await apiClient.post('dashboards/from-starter/', { key })

  // Return raw JSON:API response (normalization is the caller's job, same
  // convention as createDashboard above)
  return response.data
}

/**
 * Update an existing dashboard
 * @param {string} id - Dashboard UUID
 * @param {Object} updates - Updated dashboard data
 * @returns {Promise<Object>} Normalized updated dashboard
 */
export async function updateDashboard(id, updates) {
  const payload = {
    data: {
      type: 'dashboard',
      id,
      attributes: updates,
    },
  }

  const response = await apiClient.patch(`dashboards/${id}/`, payload)

  // Return raw JSON:API response (normalization moved to slice)
  return response.data
}

/**
 * Delete a dashboard
 * @param {string} id - Dashboard UUID
 * @returns {Promise<void>}
 */
export async function deleteDashboard(id) {
  await apiClient.delete(`dashboards/${id}/`)
}

/**
 * Update widget positions for a dashboard
 * @param {string} dashboardId - Dashboard UUID
 * @param {Array} widgets - Array of widget position updates
 * @returns {Promise<Object>} Normalized dashboard with updated widget positions
 */
export async function updateWidgetPositions(dashboardId, widgets) {
  const response = await apiClient.patch(
    `dashboards/${dashboardId}/update_widget_positions/`,
    { widgets }
  )

  // Return raw JSON:API response (normalization moved to slice)
  return response.data
}

/**
 * Toggle sharing for a dashboard (enable/disable public link)
 *
 * The backend's toggle-share action returns a plain
 * `Response({share_token: ...})` dict, but every ResourceViewSet action
 * still goes through JSONAPIRenderer, which wraps that dict as a single
 * JSON:API resource (`{data: {type, id, attributes: {share_token}}}`).
 * Unwrap it here with normalizeResponse rather than reading
 * `response.data.share_token` directly, which always finds `undefined`.
 * @param {string} dashboardId - Dashboard UUID
 * @returns {Promise<{shareToken: string | null}>}
 */
export async function toggleDashboardShare(dashboardId) {
  const response = await apiClient.post(`dashboards/${dashboardId}/toggle-share/`)
  const normalized = normalizeResponse(response)
  return { shareToken: normalized?.share_token ?? null }
}

/**
 * Clone a dashboard
 * @param {string} dashboardId - Dashboard UUID to clone
 * @param {string} [name] - Optional name for the cloned dashboard
 * @returns {Promise<Object>} Raw JSON:API response with cloned dashboard
 */
export async function cloneDashboard(dashboardId, name) {
  const payload = name ? { name } : {}
  const response = await apiClient.post(`dashboards/${dashboardId}/clone/`, payload)
  return response.data
}

/**
 * Fetch a shared dashboard by public share token (no auth required)
 * @param {string} token - Share token
 * @returns {Promise<Object>} Raw JSON:API response
 */
export async function fetchSharedDashboard(token) {
  const response = await apiClient.get(`dashboards/shared/${token}/`, {
    params: { include: 'widgets.metrics' },
  })
  return response.data
}

// --- Public Dashboard Links (PublicDashboardLink model) ---

/**
 * List active public links for a dashboard
 *
 * GET .../share/ returns a non-paginated JSON:API collection
 * (`{data: [...], links: {self}}`), the same shape any other list endpoint
 * uses. Unwrap it with normalizeResponse -- like toggleDashboardShare does
 * for its single resource -- rather than hand-parsing `data.results` or
 * `data.links`, neither of which is this shape.
 * @param {string} dashboardId - Dashboard UUID
 * @returns {Promise<Array>} Array of flat PublicDashboardLink objects
 */
export async function listPublicLinks(dashboardId) {
  const response = await apiClient.get(`dashboards/${dashboardId}/share/`, {
    params: { include: null },
  })
  return normalizeResponse(response) ?? []
}

/**
 * Create a new public link for a dashboard
 * @param {string} dashboardId - Dashboard UUID
 * @param {Object} options
 * @param {string} [options.expires_at] - ISO 8601 expiry datetime (optional)
 * @returns {Promise<Object>} Created PublicDashboardLink object
 */
export async function createPublicLink(dashboardId, { expires_at } = {}) {
  const payload = {}
  if (expires_at) {
    payload.expires_at = expires_at
  }
  const response = await apiClient.post(`dashboards/${dashboardId}/share/`, payload, {
    params: { include: null },
  })
  return response.data
}

/**
 * Revoke all active public links for a dashboard
 * @param {string} dashboardId - Dashboard UUID
 * @returns {Promise<void>}
 */
export async function revokeAllLinks(dashboardId) {
  await apiClient.delete(`dashboards/${dashboardId}/share/`, {
    params: { include: null },
  })
}

/**
 * Revoke a single public link
 * @param {string} dashboardId - Dashboard UUID
 * @param {string} linkId - PublicDashboardLink UUID
 * @returns {Promise<Object>} Updated link object
 */
export async function revokeLink(dashboardId, linkId) {
  const response = await apiClient.patch(`dashboards/${dashboardId}/share/`, {
    link_id: linkId,
    is_active: false,
  }, {
    params: { include: null },
  })
  return response.data
}

/**
 * Normalize a PublicDashboardLink resource to a stable flat shape.
 *
 * The functions above hand back a link in one of three shapes depending on
 * where it came from: a raw JSON:API envelope for a single resource
 * (`{data: {type, id, attributes}}`, from createPublicLink/revokeLink), a
 * bare JSON:API resource (`{type, id, attributes}`, not yet flattened), or
 * an already-flat object (e.g. one entry of the array listPublicLinks
 * resolves to, via normalizeResponse). This is the only place that should
 * read a link's token/id/active shape -- callers must never guess between
 * e.g. `.token` and `.share_token` themselves.
 *
 * This intentionally does not delegate to normalizeResource: that function's
 * "already flat" detection requires the object to have neither `.attributes`
 * nor `.type`, but its own output always sets `.type` -- so re-running an
 * already-flattened link back through it would misread it as an
 * unflattened resource and drop every field except id/type. Reading
 * `.attributes` when present, and the object itself otherwise, sidesteps
 * that entirely.
 * @param {Object} resource - A PublicDashboardLink in any of the shapes above
 * @returns {{id: string, token: string, isActive: boolean, expiresAt: (string|null), createdBy: *, createdAt: (string|null), url: (string|null)}|null}
 */
export function normalizePublicLink(resource) {
  if (!resource) return null

  // Unwrap a single-resource envelope ({data: {...}}) down to the resource.
  const single = (resource.data && !Array.isArray(resource.data)) ? resource.data : resource
  if (!single || single.id === undefined) return null

  const fields = single.attributes ?? single

  return {
    id: single.id,
    token: fields.token,
    isActive: fields.is_active !== false,
    expiresAt: fields.expires_at ?? null,
    createdBy: fields.created_by ?? null,
    createdAt: fields.created_at ?? null,
    url: fields.url ?? null,
  }
}
